import {
  CheckCircleOutlined as CheckCircleOutlineIcon,
  ManageSearch as ManageSearchIcon,
  RemoveCircleOutlined as RemoveCircleOutlineIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  Warning as WarningIcon,
} from '@mui/icons-material';
import {
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  FormControlLabel,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { BarChart } from '@mui/x-charts';
import {
  GridActionsCellItem,
  GridColDef,
  gridFilteredSortedRowEntriesSelector,
  GridFilterItem,
  GridRowSelectionModel,
  useGridApiRef,
} from '@mui/x-data-grid-pro';
import { useInfiniteQuery, useMutation, useQuery } from '@tanstack/react-query';
import { format, isValid, parse, subYears } from 'date-fns';
import React, {
  forwardRef,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';

import { SingleOperators } from 'common/src/query-params/operators';
import { NESTED_ROUTES } from 'common/src/routes/BaseRoutes';
import { generateDataInsightResolutionRoute } from 'common/src/routes/generateDataInsightResolutionRoute';
import { DataInsightPriority } from 'common/src/types/enums/DataInsightPriority.enum';
import { DataInsightType } from 'common/src/types/enums/DataInsightType.enum';
import { JobType } from 'common/src/types/enums/JobType.enum';
import { IBill, IDataInsight } from 'common/src/types/interfaces';

import {
  bulkUpdateDataInsights,
  getDataInsights,
  updateDataInsight,
} from 'portal/src/api/DataInsightsService';
import {
  createOperation,
  getLatestOperation,
} from 'portal/src/api/OperationsService';
import { MESSAGES } from 'portal/src/constants/snackbarMessages';
import { useTableStateContext } from 'portal/src/providers/TableProviderContext';
import { dateOnlyFormat } from 'portal/src/validators/dateValidator';

import DataInsightsFilters from './DataInsightsFilters';
import InsightDetailsPanel from './InsightDetailsPanel';

import useAuth from '../../auth/useAuth';
import useSnackbarHandler from '../../utils/Snackbar/useSnackbarHandler';
import ConfirmDialog from '../common/ConfirmDialog';
import DataTable from '../common/DataTable';
import addBooleanColumnProps from '../common/tables/addBooleanColumnProps';

// DataGrid injects `touchRippleRef` via cloneElement onto Tooltip, and Tooltip
// forwards all unknown props to its child via ...other. This wrapper discards
// it so the native span never receives an unrecognised DOM attribute.
const SpanWrapper = forwardRef<
  HTMLSpanElement,
  React.HTMLAttributes<HTMLSpanElement> & { touchRippleRef?: unknown }
>(({ touchRippleRef: _ignored, ...props }, ref) => (
  <span ref={ref} {...props} />
));

const INFINITE_QUERY_LIMIT = 1000;
const FETCH_OPERATION_INTERVAL = 2000;

interface InsightCategoryCount {
  insightCategory: DataInsightType;
  count: number;
}

export interface IRelatedDataInsightFilterValues {
  utilities: string[];
  accounts: string[];
  meters: string[];
  facilities: string[];
}

function groupByInsightCategory(data: any[]): InsightCategoryCount[] {
  const defaultValue = { insightCategory: '' as DataInsightType, count: 0 };

  if (!data || data.length === 0) {
    return [defaultValue];
  }
  return (
    data.reduce((acc: InsightCategoryCount[], item) => {
      const existingItem = acc.find(
        (entry) => entry.insightCategory === item.type,
      );
      if (existingItem) {
        existingItem.count++;
      } else {
        acc.push({ insightCategory: item.type, count: 1 });
      }
      return acc;
    }, []) ?? [defaultValue]
  );
}

function getBarChartLeftMargin(insights: InsightCategoryCount[]) {
  for (const item of insights) {
    if (
      item.insightCategory ===
        DataInsightType.ChargesOnlyMeterWithUsageLineItems &&
      item.count > 0
    ) {
      return 240;
    }
    if (
      item.insightCategory === DataInsightType.MeterUsageIncreasingOverTime &&
      item.count > 0
    ) {
      return 208;
    }
  }
  return 175;
}

function generateTypeColor(value: DataInsightType): string {
  // NOTE: these values were generated using this function
  // function generateTypeColor(value) {
  //   let hash = 0;
  //   for (let i = 0; i < value.length; i++) {
  //     hash = value.charCodeAt(i) + ((hash << 5) - hash);
  //   }
  //   const color =
  //     '#' +
  //     ((hash & 0x00ffffff) | 0x808080).toString(16).padStart(6, '0').slice(-6);
  //   return color;
  // }
  // While able to handle new DataInsightTypes, these colors will likely not meet accessibility standards for contrast
  // and should be re-evaluated
  switch (value) {
    case DataInsightType.AnomalousCost:
      return '#B2EBB6';
    case DataInsightType.AnomalousCostPerUnit:
      return '#aee8f1';
    case DataInsightType.AnomalousUsage:
      return '#9aafd8';
    case DataInsightType.BillGap:
      return '#c8cfbd';
    case DataInsightType.BillOnInactiveAccount:
      return '#a3daa0';
    case DataInsightType.BillPeriodTooLong:
      return '#c2b3ee';
    case DataInsightType.ChargesOnlyMeterWithUsageLineItems:
      return '#eff8c3';
    case DataInsightType.DateRecency:
      return '#edb0f9';
    case DataInsightType.DuplicateBill:
      return '#f6f6bc';
    case DataInsightType.DuplicateBudgetPeriods:
      return '#f9e4cc';
    case DataInsightType.InvalidBillDate:
      return '#c1c99e';
    case DataInsightType.LineItemDateOverlap:
      return '#c9a496';
    case DataInsightType.LineItemGap:
      return '#80d2d5';
    case DataInsightType.MeterAssignedToDefaultFacility:
      return '#99c58c';
    case DataInsightType.MeterNotOnBillAccount:
      return '#8ea591';
    case DataInsightType.MeterUsageIncreasingOverTime:
      return '#cfe6ae';
    case DataInsightType.MissingBudgetPeriod:
      return '#c29da2';
    case DataInsightType.MissingDemandLineItems:
      return '#ca898f';
    case DataInsightType.NoUsageLineItem:
      return '#9fe4f2';
    case DataInsightType.UsageAfterInactiveDate:
      return '#bad1c0';
    case DataInsightType.UsageAfterCurrentDate:
      return '#faeb98';
    case DataInsightType.UsageUnitChanged:
      return '#f8fcd7';
    case DataInsightType.UsageWithoutCost:
      return '#9ea0a4';
    default:
      return '#dedede';
  }
}

function generatePriorityColor(value: DataInsightPriority): string {
  switch (value) {
    case DataInsightPriority.Low:
      return '#F7F3CD'; // Extra Light Yellow
    case DataInsightPriority.Medium:
      return '#FFF59D'; // Soft Yellow
    case DataInsightPriority.High:
      return '#FFCC80'; // Soft Orange
    case DataInsightPriority.Urgent:
      return '#EF9A9A'; // Soft Red
    default:
      return '#CFD8DC'; // Neutral Grey
  }
}

const DataInsights = () => {
  const snackbarHandler = useSnackbarHandler();
  const gridApiRef = useGridApiRef();
  const { getUser, hasReadOnlyAccess } = useAuth();
  const tableStateCtx = useTableStateContext() ?? {};
  const { columns: defaultColumnVisibilityModel, filter: defaultFilter } =
    tableStateCtx[NESTED_ROUTES.SUMMARY.DATA_INSIGHTS] ?? {};
  const [filteredRowsString, setFiltersRowsString] = useState<string>(
    JSON.stringify([]),
  );
  const [refetchInterval, setRefetchInterval] = useState<number>(0);
  const [refreshDataConfirmationOpen, setRefreshDataConfirmationOpen] =
    useState<boolean>(false);
  const [filterModel, setFilterModel] = useState<any>(
    defaultFilter ?? {
      items: [
        {
          id: 1,
          field: 'date',
          operator: SingleOperators.BETWEEN,
          value: [subYears(new Date(), 1), new Date()],
        },
        {
          id: 2,
          field: 'hidden',
          operator: 'includeHidden',
          value: false,
        },
      ],
    },
  );
  const [columnVisibilityModel, setColumnVisibilityModel] = useState(
    defaultColumnVisibilityModel ?? {
      // Hide the following columns by default for better UX
      meterName: false,
      facilityName: false,
    },
  );

  const [hiddenDataInsightDialog, setHiddenDataInsightDialog] = useState<{
    open: boolean;
    id: any;
    hidden: boolean;
  }>({ open: false, id: null, hidden: false });

  const [bulkHideConfirmationOpen, setBulkHideConfirmationOpen] =
    useState<boolean>(false);
  const [selectedInsightIds, setSelectedInsightIds] =
    useState<GridRowSelectionModel>(undefined);
  const [insightsToHide, setInsightsToHide] = useState<string[]>([]);

  const {
    data: dataInsights,
    hasNextPage,
    isError,
    isFetching,
    isFetchingNextPage,
    fetchNextPage,
    refetch,
  } = useInfiniteQuery({
    queryKey: ['dataInsights'],
    queryFn: async ({ pageParam = 0 }) => {
      return await getDataInsights({
        limit: INFINITE_QUERY_LIMIT,
        page: pageParam,
      });
    },
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) => {
      if (!lastPage || !lastPage.data) return undefined;
      const totalFetched = allPages.reduce(
        (sum, page) => sum + (page.data?.length || 0),
        0,
      );
      const totalAvailable = lastPage.meta?.totalCount ?? 0;
      return totalFetched >= totalAvailable ? undefined : allPages.length;
    },
  });

  const {
    data: operation,
    isFetching: operationIsFetching,
    isPending: operationIsPending,
    refetch: refetchOperation,
  } = useQuery({
    queryKey: ['latestOperation'],
    queryFn: async () => {
      const op = await getLatestOperation(JobType.DataInsights);
      return op;
    },
    refetchInterval,
  });

  const handleRefetch = () => {
    setSelectedInsightIds({ ids: new Set(), type: 'include' });
    setInsightsToHide([]);
    refetch();
  };

  const operationIsDone =
    !operationIsPending &&
    !operationIsFetching &&
    (!operation || operation?.done === true);

  if (operationIsDone && refetchInterval === FETCH_OPERATION_INTERVAL) {
    setRefetchInterval(0);
    handleRefetch();
  }

  const {
    mutate: updateDataInsightMutation,
    isPending: isUpdateDataInsightPending,
  } = useMutation({
    mutationFn: async (payload: { id: string; hidden: boolean }) =>
      await updateDataInsight(payload.id, { hidden: payload.hidden }),
    onSuccess: (_, payload) => {
      closeHiddenDataInsightDialog();
      snackbarHandler.success(
        payload.hidden
          ? 'Insight hidden successfully.'
          : 'Insight unhidden successfully.',
      );
      handleRefetch();
    },
    onError: () => snackbarHandler.error(MESSAGES.GENERIC.ERROR),
  });

  const {
    mutate: bulkHideDataInsightsMutation,
    isPending: isBulkHideDataInsightsPending,
  } = useMutation({
    mutationFn: async (ids: string[]) => {
      await bulkUpdateDataInsights({
        insights: ids.map((id) => ({ id, hidden: true })),
      });
    },
    onSuccess: () => {
      setBulkHideConfirmationOpen(false);
      snackbarHandler.success('Selected insights hidden successfully.');
      handleRefetch();
    },
    onError: () => snackbarHandler.error('Unable to hide selected insights.'),
  });

  const [dataInsightFlattedPages, relatedFilterValues] = useMemo(() => {
    const flattenedPages =
      dataInsights?.pages.flatMap((page) => page.data || []) || [];

    let filterValues = {
      utilities: [],
      accounts: [],
      meters: [],
      facilities: [],
    } as IRelatedDataInsightFilterValues;

    if (flattenedPages.length > 0) {
      const collectedValues = flattenedPages.reduce(
        (acc, item) => {
          // Utilities
          if (item.account?.utility?.name) {
            acc.utilities.add(item.account.utility.name);
          }
          // Accounts
          if (item.account?.number) {
            acc.accounts.add(item.account.number);
          }
          // Meters
          if (item.meter?.name) {
            acc.meters.add(item.meter.name);
          }
          // Facilities
          if (item.meter?.parentFacility?.name) {
            acc.facilities.add(item.meter.parentFacility?.name);
          }
          return acc;
        },
        {
          utilities: new Set<string>(),
          accounts: new Set<string>(),
          meters: new Set<string>(),
          facilities: new Set<string>(),
        },
      );

      filterValues = {
        utilities: Array.from(collectedValues.utilities).sort(),
        accounts: Array.from(collectedValues.accounts).sort(),
        meters: Array.from(collectedValues.meters).sort(),
        facilities: Array.from(collectedValues.facilities).sort(),
      } as IRelatedDataInsightFilterValues;
    }

    return [flattenedPages, filterValues];
  }, [dataInsights?.pages]);

  useEffect(() => {
    if (hasNextPage && !isFetchingNextPage && !isFetching) {
      fetchNextPage();
    }
  }, [hasNextPage, isFetchingNextPage, fetchNextPage, isFetching]);

  const barChartData = useMemo(() => {
    const filteredRows = JSON.parse(filteredRowsString);

    const groupedData = groupByInsightCategory(
      filteredRows.map(({ model }) => model),
    );
    return groupedData;
  }, [filteredRowsString]);

  const handleFilteredRowsChanged = useCallback(() => {
    const newFilteredRows = gridApiRef.current
      ? gridFilteredSortedRowEntriesSelector(gridApiRef)
      : [];

    setFiltersRowsString(JSON.stringify(newFilteredRows));
  }, [gridApiRef]);

  useEffect(() => {
    return gridApiRef.current?.subscribeEvent(
      'stateChange',
      handleFilteredRowsChanged,
    );
  }, [handleFilteredRowsChanged, gridApiRef]);

  const handleGotoResolutionClick = (location: string | URL) => () => {
    const eventName = 'feature_used';
    const eventData = {
      feature_name: 'Data Insight Resolution',
      user_id: getUser().email,
    };

    if (window.gtag) {
      window.gtag('event', eventName, eventData);
    }

    window.open(location, '_blank');
  };

  const handleToogleToolBar = async () => {
    setFilterModel((prev) => {
      const items = prev.items.map((item) =>
        item.field === 'hidden' ? { ...item, value: !item.value } : item,
      );

      return { items };
    });
  };

  const closeHiddenDataInsightDialog = () =>
    setHiddenDataInsightDialog((prev) => ({ ...prev, open: false }));

  const handleRefreshConfirmationDialog = () =>
    setRefreshDataConfirmationOpen((prev) => !prev);

  const handleRefreshInsights = () => {
    const waitForOp = async () => {
      const op = await createOperation(JobType.DataInsights);
      if (!op.done) {
        refetchOperation();
        setRefetchInterval(FETCH_OPERATION_INTERVAL);
      }
    };

    handleRefreshConfirmationDialog();
    waitForOp();
  };

  const handleHideSelectedInsights = () => {
    const rowIds = selectedInsightIds?.ids;

    if (rowIds && rowIds.size > 0) {
      // Get the full insight objects for selected IDs and filter out already-hidden ones
      const idsToHide = dataInsightFlattedPages
        .filter((insight) => rowIds.has(insight.id) && !insight.hidden)
        .map((insight) => insight.id);

      if (idsToHide.length > 0) {
        setInsightsToHide(idsToHide);
        setBulkHideConfirmationOpen(true);
      } else {
        snackbarHandler.info('Selected insight(s) already hidden.');
      }
    }
  };

  const gridColumns: GridColDef[] = [
    {
      field: 'type',
      headerName: 'Type',
      type: 'singleSelect',
      width: 180,
      renderCell: ({ value }) => {
        return (
          <Chip
            label={value}
            style={{ backgroundColor: generateTypeColor(value) }}
          />
        );
      },
    },
    {
      field: 'priority',
      headerName: 'Priority',
      type: 'singleSelect',
      width: 96,
      renderCell: ({ value }) => {
        return (
          <Chip
            label={value}
            style={{
              backgroundColor: generatePriorityColor(
                value as DataInsightPriority,
              ),
              color: '#424242',
            }}
          />
        );
      },
    },
    {
      field: 'utilityName',
      flex: 0.6,
      headerName: 'Utility',
      type: 'singleSelect',
      valueGetter: (_, row: IDataInsight) => {
        if (!row.account) return '';
        return row.account?.utility.name;
      },
    },
    {
      field: 'accountNumber',
      flex: 0.6,
      headerName: 'Account',
      type: 'singleSelect',
      valueGetter: (_, row: IDataInsight) => {
        if (!row.account) return '';
        return row.account?.number;
      },
    },
    {
      field: 'meterName',
      flex: 0.6,
      headerName: 'Meter',
      type: 'singleSelect',
      valueGetter: (_, row: IDataInsight) => {
        if (!row.meter) return '';
        return row.meter?.name;
      },
    },
    {
      field: 'facilityName',
      flex: 0.6,
      headerName: 'Facility',
      type: 'singleSelect',
      valueGetter: (_, row: IDataInsight) => {
        if (!row.meter?.parentFacility) return '';
        return row.meter?.parentFacility?.name;
      },
    },
    {
      field: 'message',
      headerName: 'Message',
      flex: 1,
    },
    {
      field: 'date',
      headerName: 'Date',
      type: 'date',
      width: 110,
      filterOperators: [
        {
          label: SingleOperators.BETWEEN,
          value: SingleOperators.BETWEEN,
          getApplyFilterFn: (filterItem: GridFilterItem) => {
            const [initialValue, endValue] = filterItem.value;
            if (initialValue && endValue) {
              return (value): boolean =>
                value >= initialValue && value <= endValue;
            }
          },
        },
      ],
      valueFormatter: (value) => (isValid(value) ? format(value, 'P') : ''),
      valueGetter: (_, row: IDataInsight) => {
        const parsedDate = parse(row.date, dateOnlyFormat, new Date());
        return isValid(parsedDate) ? parsedDate : '';
      },
    },
    {
      field: 'hidden',
      headerName: 'Hidden',
      width: 127,
      ...addBooleanColumnProps({
        displayLogic: { valueForYes: true, yesIsGreen: false },
      }),
      filterOperators: [
        {
          label: 'Show Hidden',
          value: 'includeHidden',
          getApplyFilterFn: (filterItem: GridFilterItem) => {
            const includeHidden = filterItem.value;

            return (value): boolean => (includeHidden ? true : value === false);
          },
        },
      ],
    },
    {
      field: 'actions',
      type: 'actions',
      getActions: ({ row }: { row: IDataInsight }) => {
        const resolutionUrl = generateDataInsightResolutionRoute({
          bills: row.bills as IBill[] | undefined,
          meterId: row.meterId,
          accountId: row.accountId,
        });

        return [
          <Tooltip
            title={
              resolutionUrl
                ? 'Resolve this data insight here'
                : 'No resolution available'
            }
            key={row.id + '-resolution-tooltip'}
          >
            {/* SpanWrapper discards touchRippleRef injected by GridActionsCell */}
            <SpanWrapper>
              <GridActionsCellItem
                icon={<ManageSearchIcon className="resolution-button" />}
                label="Resolution"
                color="inherit"
                onClick={handleGotoResolutionClick(resolutionUrl)}
                disabled={!resolutionUrl}
              />
            </SpanWrapper>
          </Tooltip>,
          <Tooltip
            title={`${
              row.hidden ? 'Show' : 'Hide'
            } this data insight in the data insights view`}
            key={row.id + '-hiddenBtn-tooltip'}
          >
            <SpanWrapper>
              <GridActionsCellItem
                disabled={hasReadOnlyAccess()}
                icon={row.hidden ? <VisibilityIcon /> : <VisibilityOffIcon />}
                label={row.hidden ? 'Show' : 'Hide'}
                onClick={() =>
                  setHiddenDataInsightDialog({
                    open: true,
                    id: row.id,
                    hidden: row.hidden,
                  })
                }
              />
            </SpanWrapper>
          </Tooltip>,
        ];
      },
    },
  ];

  const refreshInProgress = operation && operation.done === false;
  const insightsLoading =
    (isFetching && !dataInsightFlattedPages.length) ||
    isFetchingNextPage ||
    refreshInProgress;

  return (
    <Stack spacing={2} sx={{ margin: 1 }}>
      <Card
        variant="outlined"
        sx={{
          p: 2,
          display: 'flex',
          justifyContent: 'space-between',
          overflow: 'visible',
        }}
      >
        <Typography
          sx={{
            alignContent: 'center',
            ...(refreshInProgress && !isFetching && { color: 'error.main' }),
          }}
        >
          {refreshInProgress && !isFetching
            ? '(Refreshing data insights, please wait...)'
            : operation
              ? `Insights last updated ${format(new Date(operation.createdAt), 'PPPp')}.`
              : null}
        </Typography>
        <Button
          color="secondary"
          disabled={insightsLoading}
          onClick={handleRefreshConfirmationDialog}
          variant="contained"
          loading={insightsLoading}
          loadingPosition="end"
        >
          Refresh Insights
        </Button>
      </Card>
      <DataInsightsFilters
        disabled={isFetching || !!hasNextPage || refreshInProgress}
        filterModel={filterModel}
        setFilterModel={setFilterModel}
        relatedFilterValues={relatedFilterValues}
      />
      <Card variant="outlined" sx={{ overflow: 'visible' }}>
        <CardContent>
          <Typography gutterBottom sx={{ mr: 1 }}>
            Number of Insights by Type
          </Typography>
          <Stack
            sx={{ alignItems: 'center', justifyContent: 'center', height: 240 }}
          >
            {insightsLoading ? (
              <CircularProgress aria-label="Loading" />
            ) : barChartData.length === 0 ? (
              <Typography color="text.secondary">
                No insights available
              </Typography>
            ) : (
              <BarChart
                sx={{ width: '100%' }}
                dataset={barChartData as any}
                grid={{ vertical: true }}
                layout="horizontal"
                margin={{ left: 0 }}
                series={[{ dataKey: 'count' }]}
                xAxis={[{ tickMinStep: 1 }]}
                yAxis={[
                  {
                    dataKey: 'insightCategory',
                    scaleType: 'band',
                    width: getBarChartLeftMargin(barChartData),
                    colorMap: {
                      type: 'ordinal',
                      values: barChartData.map((item) => item.insightCategory),
                      colors: barChartData.map((item) =>
                        generateTypeColor(item.insightCategory),
                      ),
                    },
                  },
                ]}
              />
            )}
          </Stack>
        </CardContent>
      </Card>
      <DataTable
        gridApiRef={gridApiRef}
        columns={gridColumns}
        columnVisibilityModel={columnVisibilityModel}
        contextName={NESTED_ROUTES.SUMMARY.DATA_INSIGHTS}
        dataFetchError={isError}
        disableColumnFilter
        getDetailPanelContent={({ row }: { row: IDataInsight }) => (
          <InsightDetailsPanel
            details={{
              type: row.type,
              priority: row.priority,
              utility: row.account?.utility?.name,
              account: row.account?.number,
              meter: row.meter?.name,
              facility: row.meter?.parentFacility?.name,
              message: row.message,
              resolution: row.potentialResolution,
              resolutionUrl: generateDataInsightResolutionRoute({
                bills: row.bills as IBill[] | undefined,
                meterId: row.meterId,
                accountId: row.accountId,
              }),
              date: row.date,
            }}
          />
        )}
        getDetailPanelHeight={() => 'auto'}
        filterModel={filterModel}
        isRowNavigation={false}
        loading={insightsLoading}
        onColumnVisibilityModelChange={setColumnVisibilityModel}
        onRowSelectionModelChange={setSelectedInsightIds}
        rows={dataInsightFlattedPages}
        rowSelectionModel={selectedInsightIds}
        toolbarSlotProps={{
          children: (
            <FormControlLabel
              sx={{ mr: 1 }}
              control={
                <Checkbox
                  checked={
                    filterModel?.items.find((i) => i.field === 'hidden')?.value
                  }
                  onChange={handleToogleToolBar}
                  color="primary"
                  size="small"
                />
              }
              label="Show hidden insights"
              labelPlacement="start"
            />
          ),
          exportFileName: 'data_insights',
          leftSideChildren: (
            <Button
              color="secondary"
              disabled={
                !selectedInsightIds?.ids?.size || isBulkHideDataInsightsPending
              }
              onClick={handleHideSelectedInsights}
              variant="outlined"
              size="small"
              sx={{ ml: 1 }}
            >
              Hide Selected Insights{' '}
              {!!selectedInsightIds?.ids?.size
                ? `(${selectedInsightIds.ids.size})`
                : ''}
            </Button>
          ),
          showColumns: true,
          showFilters: false,
        }}
      />
      <ConfirmDialog
        isLoading={isUpdateDataInsightPending}
        message={
          hiddenDataInsightDialog.hidden
            ? 'This data insight will become visible in the data insights view.'
            : 'This data insight will no longer be visible unless "Show Hidden Insights" is checked.'
        }
        onClose={closeHiddenDataInsightDialog}
        onPrimaryButtonClicked={() =>
          updateDataInsightMutation({
            id: hiddenDataInsightDialog.id,
            hidden: !hiddenDataInsightDialog.hidden,
          })
        }
        onSecondaryButtonClicked={closeHiddenDataInsightDialog}
        open={hiddenDataInsightDialog.open}
        primaryButtonLabel={hiddenDataInsightDialog.hidden ? 'Show' : 'Hide'}
        renderElement={
          hiddenDataInsightDialog.hidden ? (
            <CheckCircleOutlineIcon sx={{ color: 'success.main' }} />
          ) : (
            <RemoveCircleOutlineIcon sx={{ color: 'error.main' }} />
          )
        }
        secondaryButtonLabel="Cancel"
      />
      <ConfirmDialog
        isLoading={isBulkHideDataInsightsPending}
        message={
          'Selected insight(s) will no longer be visible unless "Show Hidden Insights" is checked.'
        }
        onClose={() => setBulkHideConfirmationOpen(false)}
        onPrimaryButtonClicked={() =>
          bulkHideDataInsightsMutation(insightsToHide)
        }
        onSecondaryButtonClicked={() => setBulkHideConfirmationOpen(false)}
        open={bulkHideConfirmationOpen}
        primaryButtonLabel="Hide all"
        renderElement={<RemoveCircleOutlineIcon sx={{ color: 'error.main' }} />}
        secondaryButtonLabel="Cancel"
      />
      <ConfirmDialog
        message="Refreshing data insights may take several minutes to complete. Do you want to continue?"
        onClose={handleRefreshConfirmationDialog}
        onPrimaryButtonClicked={handleRefreshInsights}
        onSecondaryButtonClicked={handleRefreshConfirmationDialog}
        open={refreshDataConfirmationOpen}
        primaryButtonLabel="Continue"
        renderElement={<WarningIcon sx={{ color: 'warning.main' }} />}
        secondaryButtonLabel="Cancel"
      />
    </Stack>
  );
};

export default DataInsights;
